import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import './imageSelector.scss';
import uploadIcon from '../../../assets/images/upload_icon3.svg';
import Toast from '../AdsPage/Common/Toast/Toast';

const ImageSelector = ({ 
  value = [], 
  onChange, 
  maxImages = 5, 
  maxFileSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastSuccess, setToastSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const showToast = (message, success = false) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToastMessage(message);
    setToastSuccess(success);
    
    // FIX: Reduced timeout from 30 minutes to 3 seconds for better UX
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage('');
      toastTimeoutRef.current = null;
    }, 3000); 
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    // FIX: Calculate how many slots are remaining
    const remainingSlots = maxImages - value.length;

    if (remainingSlots <= 0) {
      showToast(`You have reached the limit of ${maxImages} images.`, false);
      if (e.target) e.target.value = null;
      return;
    }

    // FIX: Logic to handle cases where user selects more files than allowed
    // Instead of rejecting all, we take the ones that fit.
    let filesToProcess = files;
    if (files.length > remainingSlots) {
      showToast(`Only ${remainingSlots} image(s) could be added. Limit is ${maxImages}.`, false);
      filesToProcess = files.slice(0, remainingSlots);
    }

    const validFiles = filesToProcess.filter(file => {
      // Check file type
      if (!acceptedTypes.includes(file.type)) {
        showToast(`${file.name} is not a supported image type.`, false);
        return false;
      }

      // Check file size
      if (file.size > maxFileSize) {
        showToast(`${file.name} is too large. Max ${maxFileSize / (1024 * 1024)}MB.`, false);
        return false;
      }

      // FIX: Check for duplicates to prevent state issues
      const isDuplicate = value.some(existing => existing.name === file.name && existing.file?.size === file.size);
      if (isDuplicate) {
        // Optional: fail silently or notify. We'll skip adding it.
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      const newImages = validFiles.map(file => ({
        uid: Date.now() + Math.random().toString(36).substring(2, 9), // Robust UID
        name: file.name,
        status: 'done',
        url: URL.createObjectURL(file),
        file: file,
        thumbUrl: URL.createObjectURL(file)
      }));

      // FIX: Ensure we spread the PREVIOUS value correctly to keep old files
      const updatedImages = [...value, ...newImages];
      onChange?.(updatedImages);
    }

    // Reset input to allow selecting the same file again if needed (e.g., after delete)
    if (e.target) e.target.value = null;
  };

  const handleRemove = (uid) => {
    const updatedImages = value.filter(img => img.uid !== uid);
    onChange?.(updatedImages);
  };

  const handlePreview = (file) => {
    setPreviewImage(file.url || file.thumbUrl);
    setPreviewVisible(true);
    setPreviewTitle(file.name || 'Image Preview');
  };

  const handlePreviewClose = () => {
    setPreviewVisible(false);
  };

  const uploadButton = (
    <div className="image-upload-button">
      <div className="text_xxl"><img src={uploadIcon} alt="Upload" /></div>
    </div>
  );

  return (
    <div className="image-selector ads_page">
      <div className="image-grid">
        {value.map((image, index) => (
          <div key={image.uid} className="image-item">
            <div className="image-preview">
              <img 
                src={image.url || image.thumbUrl} 
                alt={image.name} 
                className="preview-image"
              />
              <div className="image-overlay">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreview(image)}
                  className="preview-btn"
                  size="small"
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(image.uid)}
                  className="remove-btn"
                  size="small"
                  danger
                />
              </div>
            </div>
            <div className="image-info">
              <span className="image-name" title={image.name}>{image.name}</span>
              <span className="image-index">{index + 1}/{maxImages}</span> 
            </div>
          </div>
        ))}
        
        {value.length < maxImages && (
          <div className="image-item upload-item">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedTypes.join(',')}
              onChange={handleImageSelect}
              style={{ display: 'none' }} 
            />
            <div 
              className="upload-trigger"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              {uploadButton}
            </div>
          </div>
        )}
      </div>

      <div className="image-selector-info">
        <p className="info-text">
          You can select up to {maxImages} images. 
          Supported formats: {acceptedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}. 
          Maximum size: {maxFileSize / (1024 * 1024)}MB per image.
        </p>
      </div>

      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handlePreviewClose}
        width="auto"
        centered
        className="image-preview-modal image-selector-preview-popup"
        wrapClassName="image-preview-modal-wrap"
      >
        <img alt="preview" className="image-preview-modal-img" style={{ width: '100%', maxWidth: '800px' }} src={previewImage} />
      </Modal>

      {toastMessage && (
        <Toast
          message={toastMessage}
          time="3000" // Matched logic
          className={toastSuccess ? "active" : "error"}
          success={toastSuccess}
        />
      )}
    </div>
  );
};

export default ImageSelector;